import { yupResolver } from "@hookform/resolvers/yup"
import { createContext, useContext } from "react"
import type {
  Control,
  Resolver,
  UseFormHandleSubmit,
  UseFormReset
} from "react-hook-form"
import * as yup from "yup"

import { emailRegex, isValidPhoneNumber } from "@constants/validation"
import {
  AccountPageContextProps,
  accountPageDefaultValues,
  useAccountPageContext
} from "@contexts/account"
import { useAppContext } from "@contexts/app_context"
import useControlledForm from "@hooks/use_controlled_form"
import useTranslate from "@hooks/use_translate"
import i18next from "@utils/localization"

type AccountProfilePageContextProps = AccountPageContextProps & {
  control: Control<AccountPageContextProps["profileData"]>
  handleSubmit?: UseFormHandleSubmit<AccountPageContextProps["profileData"]>
  isValid?: boolean
  isDirty?: boolean
  reset?: UseFormReset<AccountPageContextProps["profileData"]>
}

const defaultValues: AccountProfilePageContextProps = {
  ...accountPageDefaultValues,
  translate: i18next.t,
  control: {} as Control<AccountPageContextProps["profileData"]>
}

const AccountProfilePageContext = createContext(defaultValues)

export function AccountProfilePageContextProvider(props: {
  children: React.ReactNode | React.ReactNode[]
}) {
  const accountPageContext = useAccountPageContext()
  const { translate, me, setMe } = useAppContext()
  const { currentLanguage } = useTranslate()

  const schema = yup
    .object({
      firstName: yup
        .string()
        .min(2, translate("first_name_must_be_atleast_2_characters"))
        .required(translate("first_name_field_is_required")),
      lastName: yup
        .string()
        .min(2, translate("last_name_must_be_atleast_2_characters"))
        .required(translate("last_name_field_is_required")),
      email: yup
        .string()
        .matches(emailRegex, translate("email_error_invalid_email"))
        .email(translate("email_error_invalid_email"))
        .required(translate("email_required")),
      language: yup.string().required(),
      phone: yup
        .object()
        .shape({
          value: yup.string().required(translate("phone_number_is_not_valid")),
          dialCode: yup.string(),
          countryCode: yup
            .string()
            .required(translate("phone_number_is_not_valid"))
        })
        .test(
          "phone_number_is_not_valid",
          translate("phone_number_is_not_valid"),
          isValidPhoneNumber
        )
    })
    .required()
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty }
  } = useControlledForm<AccountPageContextProps["profileData"]>({
    resolver: yupResolver(schema) as Resolver<
      AccountPageContextProps["profileData"]
    >,
    mode: "all",
    defaultValues: {
      firstName: me?.name || "",
      lastName: me?.surname || "",
      email: me?.email || "",
      language: currentLanguage,
      phone: {
        value: me?.phone ?? "",
        dialCode: me?.phone?.substring(0, 3),
        countryCode: "tr"
      }
    }
  })

  return (
    <AccountProfilePageContext.Provider
      value={{
        ...defaultValues,
        ...accountPageContext,
        control,
        handleSubmit,
        isValid,
        isDirty,
        reset,
        setMe
      }}
    >
      {props.children}
    </AccountProfilePageContext.Provider>
  )
}

export function useAccountProfilePageContext() {
  const context = useContext(AccountProfilePageContext)
  if (context === undefined) {
    throw new Error("AccountProfilePage Context Error")
  }
  return context
}
